<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'
import { CodedActChop, CodedMedicalAct, lateralityOptions } from '@/store/modules/medical-act/types'
import { CountryEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const { t } = useI18n()

const props = withDefaults(defineProps<{
  pmsiCountry: CountryEnum
  codedMedicalAct: CodedMedicalAct | CodedActChop
  readonly?: boolean
  rumId: number
}>(), {
  readonly: true,
})

const isLoading = ref(false)
const removeConfirm = ref(false)

const actExtention = computed(() =>
  props.pmsiCountry === CountryEnum.FR && 'extension' in props.codedMedicalAct
    ? (props.codedMedicalAct.extension ?? '')
    : '',
)

function getLateralityLabel(value: string): string {
  const option = lateralityOptions.find((lo) => lo.value === value)
  return option?.label ?? t('unknown.feminine')
}

async function removeMedicalAct() {
  isLoading.value = true
  await store.dispatch('medicalAct/removeMedicalAct', {
    id: props.codedMedicalAct.id,
    urlParams: {
      pmsiType: PmsiTypeEnum.MCO,
      summaryUnitId: props.rumId,
    },
    successMut: {
      name: 'updateMedicalActsSuccess',
    }
  })
  isLoading.value = false
  removeConfirm.value = false
}
</script>

<template>
  <div
    :style="{ opacity: (isLoading) ? 0.5 : 1 }"
  >
    <h2 class="d-flex align-items-center">
      <span class="ms-1">{{ codedMedicalAct.associatedAct.reference }}{{ actExtention }}</span>
      <sancare-octicon
        v-if="codedMedicalAct.associatedAct.isUncommon"
        v-tooltip="$t('medicalAct.uncommonWarning')"
        name="alert"
        :width="15"
        :height="15"
      />
      <small
        v-if="pmsiCountry === CountryEnum.FR && 'activity' in codedMedicalAct.associatedAct"
        class="ms-1"
      >- {{ $t('medicalAct.activity') }} {{ codedMedicalAct.associatedAct.activity }}</small>
      <small
        v-else-if="pmsiCountry === CountryEnum.CH && 'laterality' in codedMedicalAct"
        class="ms-1"
      >- {{ getLateralityLabel(codedMedicalAct.laterality) }}</small>
    </h2>
    <div class="row ps-3">
      <div class="col act-data">
        {{ codedMedicalAct.associatedAct.description }}
      </div>
      <div
        v-if="!readonly"
        class="col-auto ps-0"
      >
        <v-dropdown
          :shown="removeConfirm"
        >
          <span
            v-if="!isLoading"
          >
            <sancare-octicon
              v-tooltip="$t('medicalAct.delete')"
              name="x"
              :width="15"
              :height="15"
              class="icon-danger cp"
            />
          </span>
          <span
            v-else
            class="loader loader-xs"
          />
          <template #popper>
            <div class="p-3">
              <h4 class="title-light text-center">
                {{ $t('areYouSure') }}
              </h4>
              <div class="text-center">
                <button
                  class="btn btn-danger btn-padded"
                  @click="removeMedicalAct"
                >
                  {{ $t('confirm') }}
                </button>
              </div>
            </div>
          </template>
        </v-dropdown>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  font-size: 1.1rem;
}
</style>
